import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { handoutOverS1CellChartConfig } from '../cellChartConfigs';

/**
 * Props for the HandoutOverS1Cell component
 */
interface HandoutOverS1CellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Current fullscreen state */
  isFullscreen: boolean;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
}

/**
 * Handout Over S1 Handover Chart (LTE Cell Level)
 * 
 * Displays handover metrics for outgoing handovers over the S1 interface for individual LTE cells.
 * Shows the number of successful outgoing handovers to other cells.
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level S1 handout metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <HandoutOverS1Cell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (handoutOverS1CellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/handout-over-s1?cell_name={cellName}
 */

const HandoutOverS1Cell: React.FC<HandoutOverS1CellProps> = ({
  cellName,
  isFullscreen,
  onFullscreenToggle
}) => {
  const additionalParams = useMemo(
    () => ({
      cell_name: cellName
    }),
    [cellName]
  );

  return (
    <SiteCharts
      config={handoutOverS1CellChartConfig}
      additionalParams={additionalParams}
      isFullscreen={isFullscreen}
      onFullscreenToggle={onFullscreenToggle}
    />
  );
};

export default HandoutOverS1Cell;
